import { useState, useEffect, useMemo } from "react";
import { useItemStock } from "../state/itemStockState";

// In-memory cache: Map<skuKey, { stock: string, type: string, leadtime: string, ts: number }>
const stockCache = new Map();
const STOCK_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const clearVariantStockCache = () => stockCache.clear();

// sku: composed SKU string (e.g., PRODUCTCODE+COLOUR+SIZE+FIT) or just product code if variants absent
// orderNo required by backend query
// enabled should only be true when sku is valid and we want fresh fetch if not cached
export function useVariantStock(orderNo, sku, enabled) {
  // Resolve cached entry (fresh only)
  const cacheEntry = useMemo(() => {
    if (!sku) return null;
    return stockCache.get(sku) || null;
  }, [sku]);

  const isFresh = cacheEntry
    ? Date.now() - cacheEntry.ts < STOCK_TTL_MS
    : false;

  const [stockData, setStockData] = useState(
    () => cacheEntry || { stock: null, type: null, leadtime: null },
  );

  // Preserve stale data while revalidating
  useEffect(() => {
    if (!sku) return;
    if (cacheEntry && cacheEntry !== stockData) {
      setStockData(cacheEntry); // sync fresh or stale cache
    }
    // Do NOT clear to null when cacheEntry missing (stale-while-revalidate UX)
  }, [sku, cacheEntry]);

  const shouldFetch = enabled && !!sku && (!cacheEntry || !isFresh);
  const { data, isSuccess, isFetching, isError } = useItemStock(
    orderNo,
    sku,
    shouldFetch,
  );

  useEffect(() => {
    if (isSuccess) {
      // Safely extract values; suppress toast errors (quiet failure policy)
      const root =
        data?.data?.data?.data?.data ||
        data?.data?.data?.data ||
        data?.data?.data ||
        data?.data ||
        data;
      const stock =
        root?.freeStockQty != null ? String(root.freeStockQty) : null;
      const type = root?.replenishmentSystem ?? null;
      // Remove default / forced lead time (no 6D fallback per requirement)
      const leadtime = root?.leadTimeCalculation ?? null;
      const entry = { stock, type, leadtime, ts: Date.now() };
      stockCache.set(sku, entry);
      setStockData(entry);
    }
  }, [isSuccess, data, sku]);

  // If fetch failed, keep last known cache (silent)
  return {
    stock: stockData.stock,
    type: stockData.type,
    leadtime: stockData.leadtime,
    fetching: isFetching && !isFresh,
    error: isError,
  };
}
